import React, { useState } from 'react';
import { Alert, Linking, Modal, Platform, ScrollView, TouchableOpacity, View } from 'react-native';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToast } from 'react-native-toast-notifications';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import { Ionicons } from '@expo/vector-icons';

import { Icon } from '../../../../components/Icon/Icon';
import { Illustration } from '../../../../components/Illustration';
import SignatureView from '../../../../components/SignatureView';
import api from '../../../../utils/api';

dayjs.locale('es');


function StatusPill({ status, statusStr }) {
  const styles = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    suspended: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-200 text-gray-600',
    expired: 'bg-gray-200 text-gray-600',
  }[status] || 'bg-gray-200 text-gray-600';

  return (
    <View style={tw.style(`self-start px-3 py-1 rounded-full`, styles)}>
      <Text style={tw.style(`text-sm font-600`, styles)}>{statusStr}</Text>
    </View>
  );
}


function formatRange(auth) {
  const start = dayjs(auth.start_date);
  const end = auth.end_date ? dayjs(auth.end_date) : null;
  if (!end || start.isSame(end, 'day')) {
    return start.format('ddd D MMM YYYY');
  }
  return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
}

function formatTime(auth) {
  if (!auth.start_time) return 'Todo el día';
  const start = auth.start_time.slice(0, 5);
  const end = auth.end_time ? auth.end_time.slice(0, 5) : null;
  return end ? `De ${start} a ${end}` : `Desde ${start}`;
}


export default function AccessAuthorizationDetailModal({
  visible, authorization, communityId, justCreated = false, onRequestClose, onChanged,
}: {
  visible: boolean;
  authorization: any;
  communityId: string;
  justCreated?: boolean;
  onRequestClose: () => void;
  onChanged?: () => void;
}) {
  const insets = useSafeAreaInsets();
  const toast = useToast();
  const [isCancelling, setIsCancelling] = useState(false);

  // La firma solo viene en el detalle (show), no en el listado
  const { data: detail } = useQuery<any>({
    queryKey: ['resident.access-authorization', authorization?.uuid],
    queryFn: () => api.get(`/communities/${communityId}/access-authorizations/${authorization.uuid}`).then(r => r.data),
    enabled: !!visible && !!authorization?.uuid,
  });

  if (!authorization) return null;

  const auth = detail ?? authorization;
  // Ya pasada la fecha se nombra como en Autorizaciones anteriores: la aprobada sigue "Aprobada" y la
  // pendiente que nadie contestó es la "Vencida" (la API llama vencida a la aprobada que ya pasó)
  const isPast = dayjs(auth.end_date ?? auth.start_date).isBefore(dayjs(), 'day');
  const pastPill = !isPast ? null : ({
    approved: { status: 'approved', label: 'Aprobada' },
    pending: { status: 'expired', label: 'Vencida' },
  } as Record<string, { status: string; label: string }>)[auth.status];
  const status = auth.display_status ?? auth.status;
  const pillStatus = pastPill?.status ?? status;
  const statusStr = pastPill?.label ?? auth.status_str;
  const canCancel = auth.status === 'pending' && !isPast;
  const entryPass = auth.entry_pass;
  // qr_mode solo viene en el detalle: sin él no se muestra el QR, para no enseñarlo y luego quitarlo
  const qrMode = detail?.qr_mode;
  const entries: any[] = detail?.entry_logs ?? [];
  const files: any[] = detail?.files ?? [];

  function doCancel() {
    setIsCancelling(true);
    api.delete(`/communities/${communityId}/access-authorizations/${authorization.uuid}`)
      .then(() => {
        toast.show('Autorización cancelada');
        onChanged?.();
        onRequestClose();
      })
      .catch((error) => {
        alert(error.response?.data?.message || 'Error al cancelar');
      })
      .finally(() => setIsCancelling(false));
  }

  function confirmCancel() {
    if (Platform.OS === 'web') {
      if (confirm('¿Cancelar esta autorización?')) doCancel();
    } else {
      Alert.alert('¿Cancelar autorización?', 'Esta acción no se puede deshacer', [
        { text: 'Volver', style: 'cancel' },
        { text: 'Cancelar autorización', style: 'destructive', onPress: doCancel },
      ]);
    }
  }

  return (
    <Modal
      onRequestClose={onRequestClose}
      visible={!!visible}
      statusBarTranslucent
      animationType='slide'
    >
      <ScrollView style={tw`px-5 flex-1 bg-primary`} contentContainerStyle={tw`pb-10 pt-[${insets.top}px]`}>
        <View style={tw`row justify-between mb-4 mt-5`}>
          <View style={tw`row`}>
            <Illustration name='autorizacion' width={40} height={40} style={tw`mr-3`} />
            <Text style={tw`text-lg font-600 text-secondary`}>Autorización</Text>
          </View>
          <TouchableOpacity style={tw`items-center justify-center mr-2`} onPress={onRequestClose}>
            <Icon name='close' size={26} color={tw.color('red')} />
          </TouchableOpacity>
        </View>

        {(justCreated || canCancel) && (
          <View style={tw`bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-4 row`}>
            <Ionicons name='checkmark-circle' size={22} color={tw.color('emerald-600')} style={tw`mr-3 mt-1`} />
            <View style={tw`flex-1`}>
              {justCreated && (
                <Text style={tw`text-emerald-800 font-600 mb-1`}>Se creó la solicitud de acceso para servicio</Text>
              )}
              <Text style={tw`text-emerald-700`}>La solicitud se envió a administración para su validación.</Text>
            </View>
          </View>
        )}

        <View style={tw`bg-white rounded-2xl p-5`}>
          <View style={tw`row justify-between items-start mb-3`}>
            <Text style={tw`text-gray-400 text-sm`}>Folio {authorization.folio}</Text>
            <StatusPill status={pillStatus} statusStr={statusStr} />
          </View>

          <Text style={tw`text-secondary text-lg font-600 mb-2`}>{authorization.service_type || authorization.description}</Text>
          {!!authorization.service_type && !!authorization.description && (
            <Text style={tw`text-gray-600 mb-2`}>{authorization.description}</Text>
          )}

          <View style={tw`row mb-1`}>
            <Text style={tw`text-gray-500 font-500 w-24`}>Tipo:</Text>
            <Text style={tw`text-gray-700`}>{authorization.access_type_str}</Text>
          </View>
          <View style={tw`row mb-1`}>
            <Text style={tw`text-gray-500 font-500 w-24`}>Fecha:</Text>
            <Text style={tw`text-gray-700`}>{formatRange(authorization)}</Text>
          </View>
          <View style={tw`row mb-1`}>
            <Text style={tw`text-gray-500 font-500 w-24`}>Horario:</Text>
            <Text style={tw`text-gray-700`}>{formatTime(authorization)}</Text>
          </View>

          {authorization.provider_name && (
            <View style={tw`row mb-1`}>
              <Text style={tw`text-gray-500 font-500 w-24`}>Proveedor:</Text>
              <Text style={tw`text-gray-700`}>{authorization.provider_name}</Text>
            </View>
          )}

          {files.length > 0 && (
            <View style={tw`mt-4`}>
              <Text style={tw`text-gray-500 font-500 mb-2`}>Adjuntos</Text>
              <View style={tw`row flex-wrap gap-2`}>
                {files.map((f: any) => (
                  <TouchableOpacity key={f.uuid} onPress={() => Linking.openURL(f.url)}>
                    {f.file_type === 'pdf' ? (
                      <View style={tw`w-16 h-16 rounded-lg border border-gray-200 items-center justify-center`}>
                        <Text style={tw`text-gray-500 text-xs font-600`}>PDF</Text>
                      </View>
                    ) : (
                      <Image source={f.thumbnails?.['250']?.url ?? f.url} style={tw`w-16 h-16 rounded-lg bg-gray-100`} />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          <SignatureView xml={auth.signature} />
        </View>

        {auth.status === 'suspended' && (
          <View style={tw`bg-red-50 border border-red-200 rounded-2xl p-4 mt-4`}>
            <Text style={tw`text-red-700 font-600 mb-1`}>Autorización suspendida por la administración</Text>
            {auth.suspended_reason && <Text style={tw`text-red-700`}>{auth.suspended_reason}</Text>}
            <Text style={tw`text-red-600 text-sm mt-2`}>Tu proveedor no podrá entrar mientras siga suspendida.</Text>
          </View>
        )}

        {auth.status === 'rejected' && auth.review_comments && (
          <View style={tw`bg-red-50 border border-red-200 rounded-2xl p-4 mt-4`}>
            <Text style={tw`text-red-700 font-600 mb-1`}>Motivo del rechazo</Text>
            <Text style={tw`text-red-700`}>{auth.review_comments}</Text>
          </View>
        )}

        {status === 'approved' && qrMode === 'none' && (
          <View style={tw`bg-primary-100 border border-gray-200 px-4 py-5 rounded-xl mt-4`}>
            <Text style={tw`text-primary-800 text-lg font-500 mb-2 text-center`}>Acceso en caseta</Text>
            <Text style={tw`text-gray-600 text-center`}>
              Tu proveedor debe presentarse en caseta e identificarse. El vigilante ya tiene tu autorización; no necesita código.
            </Text>
          </View>
        )}

        {status === 'approved' && qrMode && qrMode !== 'none' && entryPass?.qr_code_url && (
          <View style={tw`bg-primary-100 border border-gray-200 px-3 py-5 rounded-xl items-center mt-4`}>
            <Text style={tw`text-primary-800 text-lg font-500 mb-3`}>Pase de acceso</Text>

            <View style={tw`my-4 p-4 bg-white rounded-lg`}>
              <Image source={entryPass.qr_code_url} style={tw`h-40 w-40`} />
            </View>

            {entryPass.short_code && (
              <View style={tw`bg-white w-90% rounded-xl items-center border border-primary-200 py-4 mb-2`}>
                <Text style={tw`text-3xl text-primary-800 font-500 tracking-[12px]`}>{entryPass.short_code}</Text>
              </View>
            )}

            <Text style={tw`text-gray-500 text-center mt-2`}>
              {qrMode === 'guard_totem'
                ? 'Comparte este código con el proveedor. Puede usarlo en el tótem o mostrarlo en caseta.'
                : 'Comparte este código con el proveedor. Debe mostrarlo al vigilante en caseta.'}
            </Text>
          </View>
        )}

        {entries.length > 0 && (
          <View style={tw`bg-white rounded-2xl p-5 mt-4`}>
            <Text style={tw`text-secondary font-600 mb-3`}>Ingresos de tu proveedor</Text>
            {entries.map((log: any, i: number) => {
              const entry = log.entry_time ? dayjs(log.entry_time) : null;
              const exit = log.exit_time ? dayjs(log.exit_time) : null;
              const day = (entry ?? exit)!;
              const people = log.extra?.people_count;

              return (
                <View key={log.uuid} style={tw.style(`row justify-between py-2`, i > 0 && `border-t border-gray-100`)}>
                  <View>
                    <Text style={tw`text-gray-700 font-500`}>{day.format('ddd D MMM')}</Text>
                    {!!people && <Text style={tw`text-gray-400 text-sm`}>{people} {people === 1 ? 'persona' : 'personas'}</Text>}
                  </View>
                  <Text style={tw`text-gray-600`}>
                    {entry ? `Entró ${entry.format('HH:mm')}` : ''}
                    {entry && exit ? ' · ' : ''}
                    {exit ? `Salió ${exit.isSame(day, 'day') ? exit.format('HH:mm') : exit.format('D MMM HH:mm')}` : (entry ? ' · Adentro' : '')}
                  </Text>
                </View>
              );
            })}
          </View>
        )}

        <View style={tw`mt-6 mb-10`}>
          <TouchableOpacity
            style={tw`bg-secondary py-4 rounded-full items-center`}
            onPress={onRequestClose}
          >
            <Text style={tw`text-white text-lg font-600`}>Cerrar</Text>
          </TouchableOpacity>

          {canCancel && (
            <TouchableOpacity
              style={tw`py-3 items-center mt-2`}
              onPress={confirmCancel}
              disabled={isCancelling}
            >
              <Text style={tw`text-gray-400 text-sm`}>
                {isCancelling ? 'Cancelando…' : 'Cancelar autorización'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </ScrollView>
    </Modal>
  );
}
